import { ProductionStreamConfigData, StreamConfigKey } from '../types';
import styles from './model-fields.module.scss';
import { useScenarioForm } from '../contexts';
import {
  DropdownField,
  NumberField,
  TanstackFieldWrapper,
} from 'components/form-fields-2';
import { useMemo } from 'react';
import { getMethodParameterMapping, getParameterLabel } from '../utils';
import classNames from 'classnames';
import { capitalize } from 'lodash-es';
import { defaultDistributions } from '../default-values';

const DISTRIBUTION_OPTIONS = ['none', 'normal', 'lognormal'] as const;

interface ModelParametersProps {
  streamKey: StreamConfigKey;
  configIndex: number;
  config: ProductionStreamConfigData;
}

export const ModelParameters = ({
  streamKey,
  configIndex,
  config,
}: ModelParametersProps) => {
  const { form } = useScenarioForm();

  const basePath = `${streamKey}[${configIndex}]`;
  const baseId = `${config.stream}-${configIndex}`;

  const isRampUpModel = config.modelType === 'ramp_up';

  const categories = useMemo(() => {
    return [
      {
        id: 'lockedParameters',
        title: 'Locked Parameters',
        parameters: config.lockedParameters,
      },
      ...(config.modelType === 'biased' && config.lossFunction === 'mlle'
        ? [
            {
              id: 'distributions',
              title: 'Distributions',
              parameters: config.distributions || defaultDistributions,
            },
          ]
        : []),
      {
        id: 'upperBounds',
        title: 'Upper Bounds',
        parameters: config.upperBounds,
      },
      {
        id: 'initialGuess',
        title: 'Initial Guess',
        parameters: config.initialGuess,
      },
      {
        id: 'lowerBounds',
        title: 'Lower Bounds',
        parameters: config.lowerBounds,
      },
    ];
  }, [config]);

  const parameters = useMemo(() => {
    return getMethodParameterMapping(config.modelType)[config.model];
  }, [config.modelType, config.model]);

  return (
    <div
      className={classNames({ [styles.rampUpModelContainer]: isRampUpModel })}
    >
      {categories.map((category) => (
        <div key={category.id}>
          <p className={styles.parametersTitle}>{category.title}</p>

          <div
            className={classNames(styles.parametersContainer, {
              [styles.rampUpParametersContainer]: isRampUpModel,
            })}
          >
            {parameters.map((parameter) => (
              <div key={parameter}>
                <form.Field
                  name={`${basePath}.${category.id}.${parameter}` as any}
                >
                  {(field) => {
                    return category.id !== 'distributions' ? (
                      <TanstackFieldWrapper<number | null> field={field}>
                        {(props) => (
                          <NumberField
                            id={`${baseId}-${parameter}`}
                            label={getParameterLabel(
                              parameter,
                              config.model,
                              config.modelType
                            )}
                            {...props}
                          />
                        )}
                      </TanstackFieldWrapper>
                    ) : (
                      <TanstackFieldWrapper<string> field={field}>
                        {(props) => (
                          <DropdownField
                            id={`${baseId}-${parameter}`}
                            label=""
                            titleText={getParameterLabel(
                              parameter,
                              config.model,
                              config.modelType
                            )}
                            items={[...DISTRIBUTION_OPTIONS]}
                            itemToString={(item: string | null) =>
                              capitalize(item || '')
                            }
                            {...props}
                          />
                        )}
                      </TanstackFieldWrapper>
                    );
                  }}
                </form.Field>
              </div>
            ))}

            {category.id !== 'lockedParameters' &&
              config.modelType === 'biased' &&
              config.lossFunction === 'mlle' &&
              config.constrainEur && (
                <form.Field name={`${basePath}.${category.id}.eurMbbl` as any}>
                  {(field) => {
                    return category.id !== 'distributions' ? (
                      <TanstackFieldWrapper<number | null> field={field}>
                        {(props) => (
                          <NumberField
                            id={`${baseId}-eurMbbl`}
                            label={getParameterLabel(
                              'eurMbbl',
                              config.model,
                              config.modelType
                            )}
                            {...props}
                          />
                        )}
                      </TanstackFieldWrapper>
                    ) : (
                      <TanstackFieldWrapper<string> field={field}>
                        {(props) => (
                          <DropdownField
                            id={`${baseId}-eurMbbl`}
                            label=""
                            titleText={getParameterLabel(
                              'eurMbbl',
                              config.model,
                              config.modelType
                            )}
                            items={[...DISTRIBUTION_OPTIONS]}
                            itemToString={(item: string | null) =>
                              capitalize(item || '')
                            }
                            {...props}
                          />
                        )}
                      </TanstackFieldWrapper>
                    );
                  }}
                </form.Field>
              )}
          </div>
        </div>
      ))}
    </div>
  );
};
